import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Initialize with English as default
  const [language, setLanguage] = useState(() => {
    // For first-time visitors, always start with English
    try {
      const saved = localStorage.getItem('website-language');
      // Only use saved language if it's explicitly set by user action
      const hasUserPreference = localStorage.getItem('user-language-preference');
      if (hasUserPreference && saved) {
        return saved;
      }
      return 'en'; // Always default to English for new visitors
    } catch (error) {
      return 'en';
    }
  });

  // Update localStorage when language changes
  useEffect(() => {
    try {
      localStorage.setItem('website-language', language);
      // Mark that user has made a language choice
      localStorage.setItem('user-language-preference', 'true');
      // Update HTML lang attribute
      document.documentElement.lang = language;
    } catch (error) {
      console.warn('Failed to save language preference');
    }
  }, [language]);

  // Ensure English is set on first load and HTML lang is updated
  useEffect(() => {
    // Set HTML lang attribute immediately
    document.documentElement.lang = language;
    
    // For new users, ensure English is the default
    try {
      const hasUserPreference = localStorage.getItem('user-language-preference');
      if (!hasUserPreference) {
        localStorage.setItem('website-language', 'en');
        localStorage.setItem('user-language-preference', 'true');
      }
    } catch (error) {
      // Ensure English fallback
      localStorage.setItem('website-language', 'en');
      localStorage.setItem('user-language-preference', 'true');
    }
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prevLang => {
      const newLang = prevLang === 'hi' ? 'en' : 'hi';
      return newLang;
    });
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    isHindi: language === 'hi'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};